import LogDirectory from "../services/logDirectory";
import LogService from "../services/logService";
import Filter from "./filter";
import Navigable from "./navigable";
import NavigatorPosition from "./navigatorPosition";
import roadService from "../services/roadService";
import roadManager from "./roadManager";
import interpolation from "../util/interpolation";
import RoadLocatorSegment from "./roadLocatorSegment";
import GpsPosition from "./gpsPosition";
import ThematicMapLayerFilters from "./thematicMapLayerFilters";
import SessionInfo from "./sessionInfo";

const logService = new LogService(LogDirectory.EnableNavigatorLogging);

export default class Navigator {
  public navigatorPosition: NavigatorPosition | null;

  public filters: Filter[] = [];
  public filtersIdRoads: number[] = [];
  public thematicMapLayerFilters: ThematicMapLayerFilters | undefined;
  public tableFilters: Filter[] = [];

  public idRoad: number | null = null;
  public idSession: number | null = null;
  public roadLocatorSegmentIndex: number = -1;
  public roadLocatorSegment: RoadLocatorSegment | null = null;
  public roadLocatorSegments: RoadLocatorSegment[] = [];
  public roadLocatorSegmentsDataItems: RoadLocatorSegment[] = [];
  public sessionInfo: SessionInfo | null = null;

  public gpsPosition: GpsPosition | null = null;

  public isPlaying: boolean = false;

  private navigables: Navigable[] = [];

  constructor() {
    this.navigatorPosition = null;
  }

  public attach(navigable: Navigable): void {
    logService.log(`${navigable.id}:navigator:attach()`);
    this.navigables.push(navigable);
  }

  public detach(navigable: Navigable): void {
    logService.log(`${navigable.id}:navigator:detach()`);
    this.navigables = this.navigables.filter(
      (observer) => observer !== navigable,
    );
  }

  private fixChainage(navigatorPosition: NavigatorPosition) {
    if (this.roadLocatorSegment) {
      navigatorPosition.chainage = interpolation.linearInterpolation(
        this.roadLocatorSegment.startDistance,
        this.roadLocatorSegment.startChainage,
        this.roadLocatorSegment.endDistance,
        this.roadLocatorSegment.endChainage,
        navigatorPosition.distance,
      );
    }
  }

  public getCurrentLocatorData(): RoadLocatorSegment[] {
    if (!this.navigatorPosition) {
      return [];
    }

    // get continous chainage range based on current position

    const dataSegments = this.roadLocatorSegmentsDataItems;

    const dataIndex = roadManager.getRoadLocatorSegmentIndex(
      this.navigatorPosition,
      dataSegments,
    );

    if (dataIndex === -1) {
      return [];
    }

    const currentDataSegment = dataSegments[dataIndex];

    const isUpChainage = currentDataSegment.isUpChainage;

    let currentContinousChainageStartIndex = dataIndex;
    let currentContinousChainageEndIndex = dataIndex;

    for (let i = dataIndex; i >= 0; i--) {
      const segment = dataSegments[i];

      if (segment.isUpChainage !== isUpChainage) {
        break;
      }

      currentContinousChainageStartIndex = i;
    }

    for (let i = dataIndex; i < dataSegments.length; i++) {
      const segment = dataSegments[i];

      if (segment.isUpChainage !== isUpChainage) {
        break;
      }

      currentContinousChainageEndIndex = i;
    }

    const currentDataSegments: RoadLocatorSegment[] = [];
    for (
      let i = currentContinousChainageStartIndex;
      i <= currentContinousChainageEndIndex;
      i++
    ) {
      const segment = dataSegments[i];
      currentDataSegments.push(segment);
    }

    // console.log("dataSegments", dataSegments);
    // console.log("currentDataSegments", currentDataSegments);

    return currentDataSegments;
  }

  public async move(navigatorPosition: NavigatorPosition, sender?: Navigable) {
    // console.log("navigator:move", navigatorPosition);

    this.navigatorPosition = navigatorPosition;

    if (this.idSession !== navigatorPosition.idSession) {
      const { data: sessionInfo } = await roadService.getSessionInfo(
        navigatorPosition.idProject,
        navigatorPosition.idSession,
      );
      this.sessionInfo = sessionInfo;
      this.idSession = navigatorPosition.idSession;
    }

    this.roadLocatorSegmentIndex = roadManager.getRoadLocatorSegmentIndex(
      navigatorPosition,
      this.roadLocatorSegments,
    );

    if (this.roadLocatorSegmentIndex === -1) {
      if (this.idRoad === navigatorPosition.idRoad) {
        this.setFilters(navigatorPosition.idProject, [], false);
      }

      let roadLocatorSegmentsData =
        await roadService.getRoadLocatorSegmentsData(
          navigatorPosition.idProject,
          navigatorPosition.idRoad,
          this.filters,
        );

      this.roadLocatorSegments = roadLocatorSegmentsData.roadLocatorSegments;
      this.roadLocatorSegmentsDataItems =
        roadLocatorSegmentsData.roadLocatorSegmentsDataItems;

      this.idRoad = navigatorPosition.idRoad;

      this.roadLocatorSegmentIndex = roadManager.getRoadLocatorSegmentIndex(
        navigatorPosition,
        this.roadLocatorSegments,
      );

      if (this.roadLocatorSegmentIndex === -1) {
        roadLocatorSegmentsData = await roadService.getRoadLocatorSegmentsData(
          navigatorPosition.idProject,
          navigatorPosition.idRoad,
          [],
        );

        this.roadLocatorSegments = roadLocatorSegmentsData.roadLocatorSegments;
        this.roadLocatorSegmentsDataItems =
          roadLocatorSegmentsData.roadLocatorSegmentsDataItems;

        this.roadLocatorSegmentIndex = roadManager.getRoadLocatorSegmentIndex(
          navigatorPosition,
          this.roadLocatorSegments,
        );
      }
    }

    this.roadLocatorSegment =
      this.roadLocatorSegments[this.roadLocatorSegmentIndex];

    if (!navigatorPosition.chainage) {
      this.fixChainage(navigatorPosition);
    }

    let navigables = this.navigables;

    const moves: Promise<void>[] = [];
    for (const navigable of navigables) {
      if (!this.navigables.find((o) => o === navigable)) {
        continue;
      }

      if (navigable !== sender) {
        if (navigable.navigatorMove) {
          const promise: Promise<void> =
            navigable.navigatorMove(navigatorPosition);
          moves.push(promise);
        }
      }
    }
    await Promise.all(moves);

    // for (const navigable of navigables) {
    //   if (!this.navigables.find((o) => o === navigable)) {
    //     continue;
    //   }

    //   if (navigable !== sender) {
    //     if (navigable.navigatorMove) {
    //       await navigable.navigatorMove(navigatorPosition);
    //     }
    //   }
    // }

    for (const navigable of navigables) {
      if (!this.navigables.find((o) => o === navigable)) {
        continue;
      }
      if (navigable.navigatorMoveAllCompleted) {
        await navigable.navigatorMoveAllCompleted();
      }
    }
  }

  public setPlaybackIsPlaying(isPlaying: boolean, sender?: Navigable): void {
    this.isPlaying = isPlaying;
    let navigables = this.navigables;
    for (const navigable of navigables) {
      if (navigable.playbackIsPlayingChanged) {
        navigable.playbackIsPlayingChanged(isPlaying);
      }
    }
  }

  public async setFilters(
    idProject: number,
    filters: Filter[],
    isProjectChanging: boolean,
    sender?: Navigable,
  ): Promise<void> {
    this.filters = filters;

    if (filters.length > 0) {
      const { data: idRoads } = await roadService.getIdRoadsForFilters(
        idProject,
        filters,
      );
      this.filtersIdRoads = idRoads;
    } else {
      this.filtersIdRoads = [];
    }

    this.roadLocatorSegments = [];
    this.roadLocatorSegmentsDataItems = [];
    this.idRoad = null;
    this.idSession = null;
    this.sessionInfo = null;

    let navigables = this.navigables;
    for (const navigable of navigables) {
      if (navigable !== sender) {
        if (navigable.setFilters) {
          await navigable.setFilters(filters, isProjectChanging);
        }
      }
    }
  }

  public async setThematicMapFilters(
    thematicMapLayerFilters: ThematicMapLayerFilters | undefined,
    sender?: Navigable,
  ): Promise<void> {
    this.thematicMapLayerFilters = thematicMapLayerFilters;

    let navigables = this.navigables;
    for (const navigable of navigables) {
      if (navigable !== sender) {
        if (navigable.setThematicMapFilters) {
          await navigable.setThematicMapFilters(thematicMapLayerFilters);
        }
      }
    }
  }

  public async setTableFilters(
    tableFilters: Filter[],
    sender?: Navigable,
  ): Promise<void> {
    this.tableFilters = tableFilters;

    let navigables = this.navigables;
    for (const navigable of navigables) {
      if (navigable !== sender) {
        if (navigable.setTableFilters) {
          await navigable.setTableFilters(tableFilters);
        }
      }
    }
  }

  public setGpsPosition(
    gpsPosition: GpsPosition | null,
    sender?: Navigable,
  ): void {
    this.gpsPosition = gpsPosition;

    let navigables = this.navigables;
    for (const navigable of navigables) {
      if (navigable !== sender) {
        if (navigable.setGpsPosition) {
          navigable.setGpsPosition(gpsPosition);
        }
      }
    }
  }
}
